import { wccModules } from 'enums/wccModules';
import { Func } from 'interfaces/func';
import { inject, injectable } from 'inversify';
import { Subscribable } from 'knockout';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import { GridColumnModel as Column, GridColumnModel } from './column';
import GridColumnPicker from './columnpickers/base';
import { GridItemModel, GridItemState as states } from './item';
import './stage.html';
import './stage.scss';

export interface GridStageVMConfig<T> {
    items: Subscribable<Array<GridItemModel<T>>>
    columnsCount: Subscribable<number>
    maxHeight: Subscribable<number>
    keepOrder: Subscribable<boolean>
    lock: Subscribable<boolean>
    columnsPicker: GridColumnPicker<T>
}

const hasNoColumn = (item: GridItemModel) => item.columnIdx() == undefined;
const hasColumn = (item: GridItemModel) => item.columnIdx() != undefined;
const isHidden = (item: GridItemModel) => item.state() === states.hidden;
const isActive = (item: GridItemModel) => item.state() === states.active;

const allowedItemStates = [states.pending, states.rendered, states.active, states.hidden];

@injectable()
export default class GridStageVM<T> {
    private columns = ko.observableArray<GridColumnModel<T>>();

    private columnsCount: Subscribable<number>
    private maxHeight: Subscribable<number>
    private keepOrder: Subscribable<boolean>
    private lock: Subscribable<boolean>

    private allItemsAreActive: Subscribable<boolean>    

    private items: Subscribable<Array<GridItemModel<T>>>
    private newItems: Subscribable<Array<GridItemModel<T>>>
    private activeItems: Subscribable<Array<GridItemModel<T>>>

    private columnsPicker: GridColumnPicker<T>

    stageColumns = ko.observableArray<GridColumnModel<T>>();

    isRendering: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: GridStageVMConfig<T>,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.columnsCount = config.columnsCount;
        this.maxHeight = config.maxHeight.mapSingle(target => this.getMaxHeight(target)).extend({ rateLimit: 0 });
        this.keepOrder = config.keepOrder;
        this.lock = config.lock;

        const allItems = config.items;
        this.allItemsAreActive = allItems.all(item => this.checkIfItemProcessed(item));
        this.items = allItems.filter(item => allowedItemStates.includes(item.state())).extend({ trackArrayChanges: true });
        this.newItems = this.items.filter(hasNoColumn);
        this.activeItems = this.items.filter(isActive);

        this.isRendering = ko.pureComputed(() => !this.allItemsAreActive());

        this.columnsPicker = config.columnsPicker;

        const isLimited = this.maxHeight.is(v => v > 0);

        effects.register([
            this.items.onArrayChange({
                remove: this.onItemRemoved.bind(this),
                move: (item, from, to) => this.shiftFromIndex(Math.min(from, to))
            }),

            ko.computed(() => this.updateStageColumns()),
            ko.computed(() => this.alignLastRow()).extend({ rateLimit: 250 }),
            ko.computed(() => this.onColumnsChanged()),
            ko.computed(() => this.limitItemsByHeight()).extend({ deferred: true })
        ]);

        effects.register(targetColumnsCount => {
            const columns = this.columns();
            const columnsCount = this.columns().length;
            const diff = targetColumnsCount - columnsCount;

            let newColumns = columns;

            if (diff < 0) {
                columns
                    .slice(targetColumnsCount)
                    .forEach(column => column.removed(true));

                newColumns = columns.slice(0, targetColumnsCount);
            } else if (diff > 0) {
                const generatedColumns = [...new Array(diff)].map((_, idx) => this.createColumn(columnsCount + idx));
                newColumns = columns.concat(generatedColumns);
            }

            this.columns(newColumns);
        }, [this.columnsCount]);

        effects.register((keepOrder, allItemsAreActive) => {
            if (keepOrder && allItemsAreActive)
                return withEffect(() => this.resetItems(), [this.items], false);
            else
                return withEffect(newItems => newItems.forEach(item => this.addItem(item)), [this.newItems, this.columns], false);
        }, [this.keepOrder, this.allItemsAreActive], false);

        ko.applyBindingsToNode(info.element, {
            'style': {
                'max-height': ko.pureComputed(() => isLimited() ? this.maxHeight() : undefined).toPx(true)
            },

            'css': {
                'grid-new__stage': true,
                'grid-new__stage--limited': isLimited
            }
        });
    }

    update() {
        this.syncItems();
    }

    reset() {
        this.resetItems();
    }

    private updateStageColumns() {
        const items = this.items();
        const newColumns = this.columns();
        const oldColumns = this.stageColumns();
        const columnsCount = this.columnsCount();

        const allowToUpdate = newColumns.length > oldColumns.length ||
            newColumns.length < oldColumns.length &&
            items.every(item => (item.columnIdx() == undefined || <number>item.columnIdx() < columnsCount) && item.attachedTo() === item.columnIdx());

        if (allowToUpdate)
            this.stageColumns(this.columns());
    }

    private syncItems() {
        this.items().forEach(item => item.update());
    }

    private async addItem(item: GridItemModel<T>) {
        await this.initItem(item);
        await item.state.when(state => state == states.rendered);

        this.shiftAfter(item);
    }

    private onItemRemoved(item: GridItemModel<T>, index: number) {
        this.shiftAfterIndex(index > 0 ? index - 1 : 0);
    }

    private initItem(item: GridItemModel<T>) {
        const columns = this.columns();
        const items = this.items();
        const idx = items.indexOf(item);

        item.update();

        if (idx !== -1) {
            const isViewItem: Func<boolean, [GridItemModel<T>]> = idx > 0 ?
                item => items.indexOf(item) < idx :
                () => false;

            const views = columns.map(column => column.getView(isViewItem));

            const columnsPicker = this.columnsPicker;
            const column = columnsPicker.pick(views);

            if (column && column.index() != item.columnIdx())
                item.columnIdx(column.index());
        }
    }

    private resetItems() {
        this.items().forEach(item => this.initItem(item));
    }

    private alignLastRow() {
        if (!this.keepOrder() && this.allItemsAreActive() && !this.lock()) {
            const columns = this.columns();
            const toReset = this.activeItems().slice(-columns.length);

            columns.forEach(column => column.height()); //track height changes

            ko.ignoreDependencies(() => toReset.forEach(item => this.initItem(item)));
        }
    }

    private limitItemsByHeight() {
        const maxHeight = this.maxHeight();

        if (maxHeight > 0) {
            this.columns().forEach(column => {
                column.items().reduce((position, item) => {
                    let bottom = position + item.height();

                    ko.ignoreDependencies(() => {
                        if ((item.state() === states.rendered || item.state() === states.active) && bottom > maxHeight)
                            item.state(states.hidden);
                        else if (item.state() === states.hidden && bottom <= maxHeight)
                            item.state(states.active);
                    });

                    return bottom;
                }, 0);
            });
        } else {
            ko.ignoreDependencies(() => this.items().filter(isHidden).forEach(item => item.state(states.active)));
        }
    }

    private shiftAfter(item: GridItemModel<T>) {
        const idx = this.items().indexOf(item);

        this.shiftAfterIndex(idx);
    }

    private shiftAfterIndex(idx: number) {
        if (idx != -1) {
            this.items()
                .slice(idx + 1) // take all cells after provided one
                .reduce((result, item) => {
                    if (!result.done) {
                        if (hasColumn(item) && item.state())
                            result.items.push(item);
                        else
                            result.done = true;
                    }

                    return result;
                }, { done: false, items: new Array<GridItemModel<T>>() }) // take all initialized cells
                .items
                .forEach(item => this.initItem(item)); //and reset them
        }
    }

    private shiftFromIndex(idx: number) {
        if (idx > 0)
            this.shiftAfterIndex(idx - 1);
        else
            this.resetItems();
    }

    private onColumnsChanged() {
        ko.ignoreDependencies(columns => this.resetItems(), this, [this.columns()]);
    }

    private createColumn(index: number) {
        return new Column({
            index: index,
            items: this.items.filter(item => item.columnIdx() == index)
        });
    }

    private getMaxHeight(target: number): number {
        if (target === 0)
            return 0;

        return this.columns().reduce((result, column) => {
            const item = _(column.items()).first();

            if (item != undefined)
                return Math.max(result, item.height());

            return result;
        }, target);
    }

    private checkIfItemProcessed(item: GridItemModel<T>) {
        return (item.state() === states.active || item.state() === states.hidden) &&
            item.columnIdx() === item.attachedTo() &&
            item.initialized();
    }
}